import styled from '@emotion/styled';
import {
  Box,
  Button,
  Dialog,
  DialogActions as _DialogActions,
  DialogContent as _DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@material-ui/core';
import AddCircleOutline from '@material-ui/icons/AddCircleOutline';
import Send from '@material-ui/icons/Send';
import { ClassAttributes, createElement, FC, MouseEvent, useContext, useEffect, useState } from 'react';
import { FormattedHTMLMessage, FormattedMessage } from 'react-intl';
import SwipeableViews from 'react-swipeable-views';

import GoogleAdAccount from '../../../entity/GoogleAdAccount';
import YoutubeChannel from '../../../entity/YoutubeChannel';
import theme from '../../../theme';
import { SPACING_REGULAR, SPACING_UNIT } from '../../constant/spacing';
import { context as dependencyContext } from '../../context/dependency';
import { context as stateContext } from '../../context/state';
import { flexibleLayoutContainer } from '../../styleUtility/flexibleLayout';
import mq from '../../styleUtility/mediaquery';
import ButtonWithIcon from '../primitive/ButtonWithIcon';
import SwipeablePanel from '../primitive/SwipeablePanel';
import GoogleAdAccountList from './GoogleAdAccountList';
import IntegrateGoogleAdAccountDialog from './IntegrateGoogleAdAccountDialog';
import YoutubeChannelList from './YoutubeChannelList';

type Props = ClassAttributes<HTMLElement> & {
  open: boolean;
  onRequestSendingCreative: (e: MouseEvent<HTMLElement>, youtubeChannel: YoutubeChannel) => void;
  onRequestAddYoutubeChannel: (e: MouseEvent<HTMLElement>) => void;
  onRequestClose: () => void;
  className?: string;
};

const CreativeSendingToYoutubeDialog: FC<Props> = ({
  open,
  onRequestClose,
  onRequestSendingCreative,
  onRequestAddYoutubeChannel,
  className,
}) => {
  const dependency = useContext(dependencyContext);
  const state = useContext(stateContext);
  const [selectedChannel, setSelectedChannel] = useState<YoutubeChannel | null>(null);
  const [index, setIndex] = useState<number>(0);
  const [linked, setLinked] = useState<boolean>(false);
  const registerToken = async () => {
    return dependency.integrateGoogleAdAccountUsecase.registerGoogleTokenWithAdwordsScope();
  };
  const onIntegrateGoogleAdAccount = async (_: any, googleAdAccount: GoogleAdAccount) => {
    await dependency.integrateGoogleAdAccountUsecase.integrateGoogleAdAccount(googleAdAccount);
  };
  const youtubeChannels = state.youtubeChannels;
  const integratedGoogleAdAccounts = state.integratedGoogleAdAccounts.adAccounts;
  const googleAdAccounts = state.googleAdAccounts;
  const lastIndex = 1;

  useEffect(() => {
    dependency.sendCreativeToYoutubeUsecase.fetchChannels();
    dependency.integrateGoogleAdAccountUsecase.fetchIntegratedGoogleAdAccounts();
    // TODO: Implementation before introducing rules
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const integrationStatus = () => {
    const count = state.integratedGoogleAdAccounts.adAccounts.count();
    if (count === 0) {
      return (
        <IntegrationStatus color="error.main">
          <FormattedHTMLMessage id="creative_sending_to_youtube_dialog.status_not_integrated" />
        </IntegrationStatus>
      );
    } else if (count === 1) {
      return (
        <IntegrationStatus color="success.main">
          <FormattedHTMLMessage id="creative_sending_to_youtube_dialog.status_integrated" />
        </IntegrationStatus>
      );
    } else {
      return (
        <IntegrationStatus color="success.main">
          <FormattedHTMLMessage
            id="creative_sending_to_youtube_dialog.status_integrated_multi"
            values={{ total: count }}
          />
        </IntegrationStatus>
      );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        onRequestClose();
        setIndex(0);
      }}
      className={className}
      maxWidth="md"
    >
      <SwipeableViews axis={theme.direction === 'rtl' ? 'x-reverse' : 'x'} index={index}>
        <SwipeablePanel value={index} index={0} dir={theme.direction}>
          <DialogTitle disableTypography>
            <Title variant="h6">
              <FormattedMessage id="creative_sending_to_youtube_dialog.title" />
            </Title>
            <Description variant="body2">
              <FormattedHTMLMessage id="creative_sending_to_youtube_dialog.description" />
            </Description>
          </DialogTitle>
          <DialogContent>
            <ContentLayout>
              <YoutubeChannelList
                youtubeChannels={youtubeChannels}
                selected={selectedChannel}
                onRequestSelectChannel={(_, channel) => setSelectedChannel(channel)}
              />
              <AddChannelButton
                icon={AddCircleOutline}
                variant="outlined"
                onClick={(e) => onRequestAddYoutubeChannel(e)}
              >
                <FormattedMessage id="creative_sending_to_youtube_dialog.add_connected_channel" />
              </AddChannelButton>
            </ContentLayout>
            <SendButton
              color="primary"
              icon={Send}
              variant="contained"
              disabled={!selectedChannel}
              onClick={async (e) => {
                await onRequestSendingCreative(e, selectedChannel!);
              }}
            >
              <FormattedMessage id="creative_sending_to_youtube_dialog.send" />
            </SendButton>
          </DialogContent>
        </SwipeablePanel>

        <SwipeablePanel value={index} index={1} dir={theme.direction}>
          <DialogTitle disableTypography>
            <Title variant="h6">
              <FormattedMessage id="creative_sending_to_youtube_dialog.integrate_ad_account_title" />
            </Title>
            <Description variant="body2">
              <FormattedHTMLMessage id="creative_sending_to_youtube_dialog.integrate_ad_account_description" />
            </Description>
          </DialogTitle>
          <DialogContent>
            {integrationStatus()}
            <ContentLayout>
              <GoogleAdAccountList googleAdAccounts={integratedGoogleAdAccounts} selected={null} onClick={() => {}} />
              <AddChannelButton
                icon={AddCircleOutline}
                variant="outlined"
                onClick={async () => {
                  const isTokenFetched = await registerToken();
                  if (isTokenFetched) {
                    setLinked(true);
                    return;
                  }
                }}
              >
                <FormattedMessage id="creative_sending_to_youtube_dialog.add_google_ad_account" />
              </AddChannelButton>
            </ContentLayout>
          </DialogContent>
        </SwipeablePanel>
        <IntegrateGoogleAdAccountDialog
          googleAdAccounts={googleAdAccounts}
          open={linked}
          onRequestSendingCreative={(e, selected) => onIntegrateGoogleAdAccount(e, selected)}
          onRequestClose={() => setLinked(false)}
        />
      </SwipeableViews>

      <Pager>
        {index !== 0 && (
          <Button
            color="default"
            onClick={() => {
              setIndex(index - 1);
            }}
          >
            <FormattedMessage id="creative_sending_to_youtube_dialog.back" />
          </Button>
        )}
        <Button
          color="default"
          onClick={() => {
            if (index === lastIndex) {
              onRequestClose();
              setIndex(0);
            } else {
              setIndex(index + 1);
            }
          }}
        >
          {index === lastIndex ? (
            <FormattedMessage id="creative_sending_to_youtube_dialog.finish" />
          ) : (
            <FormattedMessage id="creative_sending_to_youtube_dialog.skip" />
          )}
        </Button>
      </Pager>
    </Dialog>
  );
};

const Title = styled(Typography)({
  [mq('sp')]: {
    fontSize: theme.typography.subtitle1.fontSize,
  },
});

const Description = styled(DialogContentText)({
  marginTop: SPACING_REGULAR,
  [mq('sp')]: {
    fontSize: theme.typography.caption.fontSize,
    lineHeight: theme.typography.caption.lineHeight,
  },
});

const IntegrationStatus = styled(Box)({
  marginBottom: SPACING_UNIT * 2,
});

const ContentLayout = styled('div')({
  ...flexibleLayoutContainer({
    direction: 'column',
    justifyContent: 'center',
  }),
});

const DialogContent = styled(_DialogContent)({
  paddingTop: '0 !important',
  paddingBottom: 0,
});

const Pager = styled(_DialogActions)();

const AddChannelButton = styled(ButtonWithIcon)({
  marginTop: SPACING_UNIT * 2,
  textTransform: 'none',
});
const SendButton = styled(ButtonWithIcon)({
  width: '100%',
  marginTop: SPACING_UNIT * 2,
});

export default CreativeSendingToYoutubeDialog;
